import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Star } from 'lucide-react';
import { api } from '@/lib/api';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';

export default function ReviewsTab({ event, user }) {
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/events/${event.id}/reviews`);
      setReviews(r.data || []);
    } catch (err) {
      console.error('Failed to load reviews:', err);
    }
  }, [event.id]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!body.trim() && !title.trim()) return toast.error('Write a quick review');
    try {
      await api.post(`/events/${event.id}/reviews`, { rating, title, body });
      setTitle(''); setBody(''); setRating(5); load(); toast.success('Review posted');
    } catch (err) {
      console.error('Failed to post review:', err);
      toast.error(err?.response?.data?.detail || 'Failed to post review');
    }
  };

  const avg = reviews.length ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1) : null;

  return (
    <div className="space-y-3" data-testid="reviews-tab">
      <Card className="rounded-2xl p-4">
        <div className="flex items-center gap-3">
          <div className="font-serif text-3xl font-semibold">{avg ?? '—'}</div>
          <div>
            <div className="flex">{[1,2,3,4,5].map((n) => <Star key={n} className={`h-4 w-4 ${avg && n <= Math.round(avg) ? 'fill-amber-500 text-amber-500' : 'text-muted-foreground'}`} />)}</div>
            <div className="text-xs text-muted-foreground">{reviews.length} review{reviews.length === 1 ? '' : 's'}</div>
          </div>
        </div>
      </Card>
      <Card className="rounded-2xl p-3 space-y-2">
        <div className="flex gap-1" data-testid="review-stars">
          {[1,2,3,4,5].map((n) => (
            <button key={n} onClick={() => setRating(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} aria-label={`${n} stars`} data-testid={`review-star-${n}`}>
              <Star className={`h-6 w-6 ${(hover || rating) >= n ? 'fill-amber-500 text-amber-500' : 'text-muted-foreground'}`} />
            </button>
          ))}
        </div>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" data-testid="review-title-input" />
        <Textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Share your experience…" data-testid="review-body-input" />
        <Button onClick={submit} data-testid="review-submit" className="rounded-xl gradient-fab text-white">Post review</Button>
      </Card>
      {reviews.map((r) => (
        <Card key={r.id} className="rounded-2xl p-4" data-testid={`review-${r.id}`}>
          <div className="flex items-center gap-2 mb-1">
            <Avatar className="h-8 w-8"><AvatarImage src={r.user?.picture} /><AvatarFallback>{(r.user?.name || 'U').slice(0,1)}</AvatarFallback></Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{r.user?.name || 'Anonymous'}</div>
              <div className="text-[11px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</div>
            </div>
            <div className="flex">{[1,2,3,4,5].map((n) => <Star key={n} className={`h-3.5 w-3.5 ${n <= r.rating ? 'fill-amber-500 text-amber-500' : 'text-muted-foreground'}`} />)}</div>
          </div>
          {r.title && <div className="font-semibold text-sm mt-1">{r.title}</div>}
          {r.body && <div className="text-sm text-foreground/90 mt-1 whitespace-pre-line">{r.body}</div>}
        </Card>
      ))}
    </div>
  );
}
